import * as React from 'react';
import { ReactNode } from 'react';

import { unstable_createMuiStrictModeTheme as createMuiTheme, ThemeProvider } from '@material-ui/core';
import { BreakpointValues } from '@material-ui/core/styles/createBreakpoints';

import theme from '@/theme';

const { palette, textColors, actionColors } = theme;

export const defaultTheme = createMuiTheme({
  typography: {
    htmlFontSize: theme.htmlFontSize,
    fontFamily: ['Roboto', 'Helvetica Neue', 'Helvetica', 'sans-serif'].join(','),
    button: {
      textTransform: 'none',
    },
  },
  breakpoints: {
    values: theme.breakpoints as BreakpointValues,
  },
  palette: {
    primary: {
      main: palette.primary,
    },
    secondary: {
      main: palette.secondary,
    },
    text: {
      primary: textColors.primary,
    },
    action: {
      hover: actionColors.hover,
    },
    grey: {
      ...palette.gray,
    },
  },
  props: {
    MuiButton: {
      disableRipple: true,
    },
    MuiButtonBase: {
      disableRipple: true,
    },
  },
  overrides: {
    MuiButton: {
      contained: {
        boxShadow: '3px 3px 7px rgba(0, 0, 0, 0.1);',
      },
    },
    MuiTooltip: {
      arrow: {
        color: palette.primary,
      },
      tooltip: {
        backgroundColor: palette.primary,
      },
    },
    MuiExpansionPanel: {
      root: {
        MuiExpanded: {
          marginTop: 0,
          marginBottom: 0,
        },
      },
    },
    MuiInputBase: {
      formControl: {
        '&$focused': {
          backgroundColor: palette.blue['100'],
        },
        '&.required .MuiSelect-icon': {
          fill: palette.secondary,
        },
      },
    },
    MuiFormLabel: {
      root: {
        color: palette.gray['700'],
      },
      filled: {
        '&$focused': {
          color: palette.purple['700'],
        },
      },
    },
    MuiFormControl: {
      root: {
        '&.optional label': {
          color: palette.gray['500'],
        },
      },
    },
    MuiRadio: {
      root: {
        color: palette.gray['300'],
      },
      colorSecondary: {
        '&$checked': {
          color: palette.blue['600'],
        },
      },
    },
    MuiCheckbox: {
      colorPrimary: {
        '&$checked': {
          color: palette.blue['600'],
        },
      },
    },
    MuiMenu: {
      paper: {
        border: '1px solid',
        borderColor: palette.gray['300'],
        backgroundColor: palette.gray['100'],
      },
      list: {
        padding: 0,
      },
    },
    MuiMenuItem: {
      root: {
        color: palette.blue['900'],
        borderLeft: 'none',
        borderRight: 'none',
        borderTop: '1px solid',
        borderBottom: '1px solid',
        borderTopColor: palette.gray['300'],
        borderBottomColor: palette.gray['300'],
        paddingTop: '1rem',
        paddingBottom: '1rem',
        paddingLeft: '1.5rem',
        paddingRight: '2rem',
      },
      // Dense menu items are used in tandem with submenus (less padding between items)
      dense: {
        border: 'none',
        paddingTop: '0.3rem',
        paddingBottom: '0.3rem',
      },
    },
  },
});

export function MaterialThemeProvider({ children }: { children: ReactNode }) {
  return (
    <ThemeProvider theme={defaultTheme}>
      {children}
    </ThemeProvider>
  );
}
